import { useEffect, useState, type ReactNode } from "react";
import { CalendarClock } from "lucide-react";
import {
  EmptyState,
  GlossaryText,
  SkeletonText,
  StatusStepper,
  type StepperStep,
} from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { tpsCycleApi, type TpsMonthlyCycle } from "../api/tpsCycle";

// SP-2b — "This month's cycle": the per-loan monthly money-cycle mirror
// rendered inside the TPS Loans tab's loan detail. Self-loading by loanId
// with a month picker over the server's availablePeriods. Read-only — every
// step's action lives on its own tab (Bills / Distributions); this just
// tells the firm where the month stands and what's next FOR THEM. No React
// Query in the portal — plain useState + useEffect.

const STEP_LABEL: Record<string, string> = {
  statement: "Bill prepared",
  "phoenix-review": "Phoenix review",
  "tps-review": "Your review",
  "borrower-pays": "Borrower paying",
  "payment-recorded": "Payment recorded",
  payout: "Payout to you",
  complete: "Cycle complete",
};

const SELECT_CLASS =
  "h-9 appearance-none rounded-md border border-border-light bg-white-pure px-space-2 text-small text-onyx focus-visible:outline-none focus-visible:shadow-goldRing";

/** "2026-06" → "June 2026". */
function monthLabel(period: string): string {
  const d = new Date(`${period}-01T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return period;
  return d.toLocaleString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

/** ONE plain-language "what's next for you" sentence, keyed off the
 *  active/blocked step (TPS perspective). */
function nextAction(cycle: TpsMonthlyCycle): string {
  const current = cycle.steps.find((s) => s.state === "active" || s.state === "blocked");
  if (!current) return "This month's cycle is complete.";
  switch (current.key) {
    case "statement":
      return "Phoenix is preparing this month's bill.";
    case "phoenix-review":
      return "Phoenix is reviewing this month's bill before it reaches you.";
    case "tps-review":
      return cycle.statement
        ? `A bill for ${formatCurrency(cycle.statement.amountDue)} is waiting for your review on the Bills tab.`
        : "A bill is waiting for your review on the Bills tab.";
    case "borrower-pays":
      return "The bill is posted — the borrower is paying. Bank transfers take 3–5 business days to clear.";
    case "payment-recorded":
      return "The borrower's payment cleared — Phoenix is recording it against the loan.";
    case "payout":
      if (current.state === "blocked") {
        return cycle.distribution?.blockReason
          ? `Payout is waiting: ${cycle.distribution.blockReason}`
          : "Payout is waiting to be released.";
      }
      return cycle.distribution
        ? `A payout of ${formatCurrency(cycle.distribution.totalAmount)} is ready to approve on the Distributions tab.`
        : "A payout is ready to approve on the Distributions tab.";
    case "complete":
    default:
      return "This month's cycle is complete.";
  }
}

export function TpsCycleMirror({ loanId }: { loanId: string }) {
  const [cycle, setCycle] = useState<TpsMonthlyCycle | null>(null);
  // The month picker survives a period change (the newly loading cycle would
  // otherwise unmount it), so periods are kept beside the cycle itself.
  const [periods, setPeriods] = useState<string[]>([]);
  const [period, setPeriod] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    void (async () => {
      try {
        const data = await tpsCycleApi.get(loanId, period ?? undefined);
        if (cancelled) return;
        setCycle(data ?? null);
        if (data?.availablePeriods?.length) setPeriods(data.availablePeriods);
      } catch {
        if (!cancelled) {
          setFailed(true);
          setCycle(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loanId, period]);

  const showEmpty = !loading && (failed || !cycle || cycle.steps.length === 0);

  let stepper: ReactNode = null;
  if (!loading && cycle && cycle.steps.length > 0) {
    const steps: StepperStep[] = cycle.steps.map((s) => ({
      key: s.key,
      label: STEP_LABEL[s.key] ?? s.key,
    }));
    const activeIdx = cycle.steps.findIndex(
      (s) => s.state === "active" || s.state === "blocked",
    );
    const allDone = cycle.steps.every((s) => s.state === "done");
    const currentIndex =
      activeIdx >= 0
        ? activeIdx
        : allDone
          ? cycle.steps.length - 1
          : Math.max(cycle.steps.findIndex((s) => s.state === "upcoming"), 0);
    stepper = (
      <div className="flex flex-col gap-space-3">
        <StatusStepper
          steps={steps}
          currentIndex={currentIndex}
          complete={activeIdx === -1 && allDone}
          label="Monthly cycle"
          data-testid="tps-cycle-stepper"
        />
        <p className="m-0 text-small text-onyx" data-testid="tps-cycle-next">
          <GlossaryText text={nextAction(cycle)} />
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-space-3" data-testid="tps-cycle-mirror">
      {periods.length > 0 && (
        <label className="flex items-center gap-space-2 self-start text-caption text-gray-mid">
          <span className="uppercase tracking-label">Month</span>
          <select
            value={period ?? cycle?.period ?? periods[0]}
            onChange={(e) => setPeriod(e.target.value)}
            aria-label="Month"
            data-testid="tps-cycle-period"
            className={SELECT_CLASS}
          >
            {periods.map((p) => (
              <option key={p} value={p}>
                {monthLabel(p)}
              </option>
            ))}
          </select>
        </label>
      )}

      {loading && <SkeletonText lines={3} data-testid="tps-cycle-loading" />}

      {showEmpty && (
        <EmptyState
          icon={CalendarClock}
          title="No cycle activity for this month yet."
          description="Once Phoenix prepares this month's bill, the cycle will appear here."
        />
      )}

      {stepper}
    </div>
  );
}
